/**
 * Archivo: useWindowSize.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-07-10
 * 
 * Descripción:
 * Este archivo define un hook personalizado `useWindowSize` que rastrea el tamaño de la ventana del navegador. 
 * El hook devuelve un objeto con las dimensiones actuales de la ventana (`width` y `height`), y se actualiza 
 * automáticamente cuando la ventana se redimensiona.
 * 
 * Dependencias:
 * - react: ^18.2.0 (Para usar los hooks `useState` y `useEffect`)
 * 
 * Propósito:
 * Proporcionar un mecanismo reutilizable que permite a los componentes de React acceder al tamaño actual de la 
 * ventana del navegador y reaccionar a los cambios en el tamaño de la ventana.
 *
 * ```
 */

import { useState, useEffect } from 'react';

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

export default useWindowSize;
